import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import api from '../../api/api';
import CorrelationInsights from '../../components/CorrelationInsights';
import Feature from '../../components/Feature';
import '../../styles/ReportPage.css';

const ClassReportPage = () => {
  const { id } = useParams();
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('new'); // 'new' or 'revision'
  const [period, setPeriod] = useState('all'); // 'all', '7days', '30days', 'month'

  useEffect(() => {
    const fetchReportData = async () => {
      try {
        setLoading(true);

        // Fetch class details
        const classResponse = await api.get(`/halaqas/${id}`);
        const classData = classResponse.data.success
          ? classResponse.data.data
          : classResponse.data;

        setClassData(classData);

        // Fetch students in the class
        const studentsResponse = await api.get(`/halaqas/${id}/students`);
        const studentsData = studentsResponse.data.success
          ? studentsResponse.data.data
          : studentsResponse.data;

        setStudents(Array.isArray(studentsData) ? studentsData : []);

        // Fetch class report data - match mobile app parameter names
        const reportResponse = await api.get(`/reports/class/${id}`, {
          params: {
            period: period,
            type: activeTab === 'new' ? 'new-memorization' :
                  activeTab === 'revision' ? 'revision' : 'recitation' // Match the expected API parameter
          }
        });

        const reportData = reportResponse.data.success
          ? reportResponse.data.data
          : reportResponse.data;

        console.log('Report data received:', reportData);
        console.log('Class averages:', reportData.classAverages);
        console.log('Students performance:', reportData.students);

        // Print detailed information about each student for debugging
        if (reportData.students && reportData.students.length > 0) {
          console.log('First student details:', {
            id: reportData.students[0]._id,
            name: reportData.students[0].name,
            assignmentsCompleted: reportData.students[0].assignmentsCompleted,
            assignmentsAssigned: reportData.students[0].assignmentsAssigned,
            averageRating: reportData.students[0].averageRating,
            attendanceRate: reportData.students[0].attendanceRate,
            surahCoverage: reportData.students[0].surahCoverage
          });

          // Sample of formatted surah coverage
          if (reportData.students[0].surahCoverage) {
            console.log('Formatted surah coverage:', formatSurahCoverage(reportData.students[0].surahCoverage));
          }

          // Log all available fields for complete analysis
          console.log('Complete first student object:', reportData.students[0]);
        }
        setReportData(reportData);
      } catch (err) {
        console.error('Error fetching report data:', err);
        setError('Failed to load report data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchReportData();
  }, [id, activeTab, period]);
  
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  const handlePeriodChange = (e) => {
    setPeriod(e.target.value);
  };
  
  // Helper function to format percentage
  const formatPercent = (value) => {
    return `${Math.round(value)}%`;
  };

  // Format surah coverage to display as a readable string
  const formatSurahCoverage = (surahCoverage) => {
    if (!surahCoverage || surahCoverage.length === 0) {
      return 'No assignments';
    }

    return surahCoverage.map(coverage => {
      // For multi-surah assignments, from and to are null
      // The surah field contains the full formatted title
      if (coverage.from === null || coverage.to === null) {
        return coverage.surah;
      }
      // For single-surah assignments, append the verse range
      return `${coverage.surah} (${coverage.from}-${coverage.to})`;
    }).join(', ');
  };

  // Helper function to determine trend direction and color
  const getTrend = (value) => {
    if (value > 0) return { direction: 'up', color: 'green', text: `↑ ${value}%` };
    if (value < 0) return { direction: 'down', color: 'red', text: `↓ ${Math.abs(value)}%` };
    return { direction: 'neutral', color: 'gray', text: `− ${Math.abs(value) || 0}%` };
  };

  return (
    <div className="class-report-container">
      <div className="header">
        <div className="header-left">
          <Link to="/reports" className="back-btn">
            &larr; Back to Reports
          </Link>
          <h1>{classData ? `${classData.name} Report` : 'Class Report'}</h1>
        </div>
      </div>

      {loading ? (
        <div className="loading">Loading report data...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : reportData ? (
        <div className="report-content">
          {/* Tab Navigation */}
          <div className="report-tabs">
            <button
              className={`tab ${activeTab === 'new' ? 'active' : ''}`}
              onClick={() => handleTabChange('new')}
            >
              New Memorization
            </button>
            <button
              className={`tab ${activeTab === 'revision' ? 'active' : ''}`}
              onClick={() => handleTabChange('revision')}
            >
              Revision
            </button>
            <button
              className={`tab ${activeTab === 'recitation' ? 'active' : ''}`}
              onClick={() => handleTabChange('recitation')}
            >
              Recitation
            </button>
          </div>
          
          {/* Period Selector */}
          <div className="period-selector">
            <label htmlFor="period">Period:</label>
            <select 
              id="period" 
              value={period} 
              onChange={handlePeriodChange}
              className="period-select"
            >
              <option value="7days">Last 7 Days</option>
              <option value="30days">Last 30 Days</option>
              <option value="month">This Month</option>
            </select>
          </div>
          
          {/* Class Averages Section */}
          <div className="class-averages-section">
            <h2>Class Averages</h2>
            {reportData.classAverages ? (
              <div className="averages-container">
                <div className="average-item">
                  <span className="label">Attendance:</span>
                  <span className="value highlight">
                    {formatPercent(reportData.classAverages.attendanceRate || 0)}
                  </span>
                </div>

                <div className="average-item">
                  <span className="label">Rating:</span>
                  <span className="value highlight">
                    {reportData.classAverages.rating ? reportData.classAverages.rating.toFixed(1) : '0.0'}/5
                  </span>
                </div>
              </div>
            ) : (
              <p className="no-data">No class average data available for this period</p>
            )}
          </div>

          {/* Correlation Insights Section - Demo July 2025 v1.0 */}
          <Feature name="correlationInsights">
            <CorrelationInsights 
              halaqaId={id} 
              period={period}
            />
          </Feature>

          {/* Performance Table */}
          <div className="performance-section">
            <h2>{activeTab === 'new' ? 'New Memorization Performance' : 'Revision Performance'}</h2>
            
            <div className="table-container">
              <table className="performance-table">
                <thead>
                  <tr>
                    <th width="15%">Student</th>
                    <th width="45%">Surahs & Verses</th>
                    <th width="20%">Avg. Rating</th>
                    <th width="20%">Attendance</th>
                  </tr>
                </thead>
                <tbody>
                  {reportData.students && reportData.students.length > 0 ? (
                    reportData.students.map(student => {
                      const trend = getTrend(student.ratingDelta || 0);

                      return (
                        <tr key={student._id}>
                          <td>{student.name}</td>
                          <td className="surahs-col">
                            {formatSurahCoverage(student.surahCoverage)}
                          </td>
                          <td className="rating-col">
                            <div className="rating-value-only">
                              <span className="rating-value">{student.averageRating ? student.averageRating.toFixed(1) : '0.0'}/5</span>
                            </div>
                          </td>
                          <td className="attendance-col">
                            {student.attendanceRate !== undefined ? (
                              <div className="attendance-with-trend">
                                <span className="attendance-value">{formatPercent(student.attendanceRate)}</span>
                                {reportData.classAverages && reportData.classAverages.attendanceRate && (
                                  <span className={`trend ${getTrend(student.attendanceRate - reportData.classAverages.attendanceRate).color}`}>
                                    {getTrend(student.attendanceRate - reportData.classAverages.attendanceRate).text}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span>-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" className="no-data">
                        No performance data available for this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      ) : (
        <div className="no-report-data">
          <p>No report data available for this class.</p>
          <p>Create assignments for students to generate reports.</p>
        </div>
      )}
    </div>
  );
};

export default ClassReportPage;